import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Country {
  id: number;
  code: string;
  name: string;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface CountryFilterOption {
  value: string;
  label: string;
  code?: string;
  avatar_url?: string;
}
// Tipos de respuesta
interface CountriesResponse {
  data: Country[];
  totalCount: number;
}
interface CountriesState extends FilteredState<Country> {
  selectedCountry: Country | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: CountryFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: CountriesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'name',
  sortDirection: 'asc',
  searchTerm: null,
  selectedCountry: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar countries con paginación y filtros (para vistas de lista)
export const fetchCountries = createAsyncThunk<CountriesResponse, any>('countries/fetchCountries', async (params) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, código) - CACHEADO
export const fetchCountryFilterOptions = createAsyncThunk<CountryFilterOption[], void>(
  'countries/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const countriesState = state.countries as CountriesState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      countriesState.filterOptionsLastFetch &&
      Date.now() - countriesState.filterOptionsLastFetch < CACHE_DURATION &&
      countriesState.filterOptions.length > 0
    ) {
      return countriesState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/countries?startRow=0&endRow=300&sortProperty=name&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const countries = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: CountryFilterOption[] = countries.map((country: any) => ({
        value: country.id.toString(),
        label: country.name,
        code: country.code,
        avatar_url: country.avatar_url
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchCountryById = createAsyncThunk<Country, number>('countries/fetchCountryById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createCountry = createAsyncThunk<Country, Partial<Country>>('countries/createCountry', async (country) => {
  throw new Error('Not implemented yet');
});
export const updateCountry = createAsyncThunk<{ id: number; data: Country }, { id: number; data: Partial<Country> }>('countries/updateCountry', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteCountry = createAsyncThunk<number, number>('countries/deleteCountry', async (id) => {
  throw new Error('Not implemented yet');
});
const countriesSlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectCountry: (state, action: PayloadAction<Country | null>) => {
      state.selectedCountry = action.payload;
    },
    clearSelection: (state) => {
      state.selectedCountry = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchCountries (lista completa)
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar países';
      })
      // Casos para fetchCountryFilterOptions (opciones cacheadas)
      .addCase(fetchCountryFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchCountryFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchCountryFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectCountryFilterOptions = (state: any) => state.countries.filterOptions;
export const selectCountryFilterOptionsLoading = (state: any) => state.countries.filterOptionsLoading;
export const selectCountryFilterOptionsError = (state: any) => state.countries.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areCountryFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.countries.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectCountry, clearSelection } = countriesSlice.actions;
export default countriesSlice.reducer;
